import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateEmpBenefitSettlement,
  CreateEmpBenefitValuationV2,
  EmpBenefitDisclosure,
  EmpBenefitMonthlyEntry,
  EmpBenefitMonthlyEntryListRow,
  EmpBenefitSettlementRow,
  EmpBenefitValuation,
  EmpBenefitWhatIf,
  EmpBenefitWhatIfResult,
  RunEmpBenefitMonthly,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// Cache-key scopes — companyId-keyed per project convention.
const valuationsKey = (companyId: string | undefined) =>
  ['emp-benefit-valuations', companyId] as const;
const monthlyKey = (companyId: string | undefined) => ['emp-benefit-monthly', companyId] as const;
const settlementsKey = (companyId: string | undefined) =>
  ['emp-benefit-settlements', companyId] as const;
const disclosureKey = (companyId: string | undefined, year: number) =>
  ['emp-benefit-disclosure', companyId, year] as const;

export interface EmpBenefitValuationsFilters {
  fromDate?: string;
  toDate?: string;
}

export function useEmpBenefitValuations(
  companyId: string | undefined,
  filters: EmpBenefitValuationsFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.fromDate) qs.set('fromDate', filters.fromDate);
  if (filters.toDate) qs.set('toDate', filters.toDate);
  const search = qs.toString();
  return useQuery({
    queryKey: [...valuationsKey(companyId), filters],
    queryFn: () =>
      api<EmpBenefitValuation[]>(
        `/companies/${companyId}/emp-benefit/valuations${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useEmpBenefitValuation(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: [...valuationsKey(companyId), 'detail', id],
    queryFn: () => api<EmpBenefitValuation>(`/companies/${companyId}/emp-benefit/valuations/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Upload (or replace) the signed actuarial-report PDF on a valuation. Multipart
// FormData — the api() helper lets the browser set the boundary.
export function useUploadEmpBenefitValuationPdf(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      return api<{ pdfStorageKey: string; pdfSha256: string; pdfUploadedAt: string }>(
        `/companies/${companyId}/emp-benefit/valuations/${id}/attachment`,
        { method: 'POST', body: fd },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: valuationsKey(companyId) }),
  });
}

// One-shot fetch of a short-lived presigned download URL for the bound report.
export function getEmpBenefitValuationPdfUrl(
  companyId: string,
  id: string,
): Promise<{ url: string; expiresAt: string }> {
  return api<{ url: string; expiresAt: string }>(
    `/companies/${companyId}/emp-benefit/valuations/${id}/attachment/url`,
  );
}

export function useCreateEmpBenefitValuation(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEmpBenefitValuationV2) =>
      apiJson<CreateEmpBenefitValuationV2, EmpBenefitValuation>(
        'POST',
        `/companies/${companyId}/emp-benefit/valuations`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: valuationsKey(companyId) }),
  });
}

export function useEmpBenefitMonthly(companyId: string | undefined, period?: string) {
  const qs = new URLSearchParams();
  if (period) qs.set('period', period);
  const search = qs.toString();
  return useQuery({
    queryKey: [...monthlyKey(companyId), period ?? null],
    queryFn: () =>
      api<EmpBenefitMonthlyEntryListRow[]>(
        `/companies/${companyId}/emp-benefit/monthly${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

// ----- Settlements (standalone severance events) -----

export function useEmpBenefitSettlements(companyId: string | undefined) {
  return useQuery({
    queryKey: settlementsKey(companyId),
    queryFn: () =>
      api<EmpBenefitSettlementRow[]>(`/companies/${companyId}/emp-benefit/settlements`),
    enabled: !!companyId,
  });
}

export function useCreateEmpBenefitSettlement(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEmpBenefitSettlement) =>
      apiJson<CreateEmpBenefitSettlement, EmpBenefitSettlementRow>(
        'POST',
        `/companies/${companyId}/emp-benefit/settlements`,
        data,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: settlementsKey(companyId) }),
  });
}

// ----- Reporting (disclosure + what-if sensitivity) -----

export function useEmpBenefitDisclosure(companyId: string | undefined, year: number) {
  return useQuery({
    queryKey: disclosureKey(companyId, year),
    queryFn: () =>
      api<EmpBenefitDisclosure>(`/companies/${companyId}/emp-benefit/disclosure?year=${year}`),
    enabled: !!companyId,
  });
}

export function useEmpBenefitWhatIf(companyId: string | undefined) {
  return useMutation({
    mutationFn: (data: EmpBenefitWhatIf) =>
      apiJson<EmpBenefitWhatIf, EmpBenefitWhatIfResult>(
        'POST',
        `/companies/${companyId}/emp-benefit/sensitivity/whatif`,
        data,
      ),
  });
}

// ----- Central approval-engine actions (submit / approve / reject / return) -----
// Generic over the doc kind so monthly, settlements and remeasurements share one
// lifecycle. A final-step approve posts the JV server-side, so success
// invalidates the lists + disclosure + journal-entries.

export type EmpBenefitDocKind = 'monthly' | 'settlements' | 'remeasurements';

export interface EmpBenefitDecideInput {
  id: string;
  action: 'approve' | 'reject' | 'return';
  step: number;
  comment?: string;
  reason?: string;
}

function invalidateEmpBenefit(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
): void {
  void qc.invalidateQueries({ queryKey: ['emp-benefit-monthly', companyId] });
  void qc.invalidateQueries({ queryKey: ['emp-benefit-settlements', companyId] });
  void qc.invalidateQueries({ queryKey: ['emp-benefit-disclosure', companyId] });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
}

export function useEmpBenefitSubmit(companyId: string | undefined, kind: EmpBenefitDocKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<Record<string, never>, unknown>(
        'POST',
        `/companies/${companyId}/emp-benefit/${kind}/${id}/submit`,
        {},
      ),
    onSuccess: () => invalidateEmpBenefit(qc, companyId),
  });
}

export function useEmpBenefitDecide(companyId: string | undefined, kind: EmpBenefitDocKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: EmpBenefitDecideInput) => {
      const body: Record<string, string | number> =
        v.action === 'approve'
          ? { step: v.step, comment: v.comment ?? '' }
          : { step: v.step, reason: v.reason ?? '' };
      return apiJson<Record<string, string | number>, unknown>(
        'POST',
        `/companies/${companyId}/emp-benefit/${kind}/${v.id}/${v.action}`,
        body,
      );
    },
    onSuccess: () => invalidateEmpBenefit(qc, companyId),
  });
}

export function useEmpBenefitReverse(companyId: string | undefined, kind: EmpBenefitDocKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; reversalPeriod: string }) =>
      apiJson<{ reversalPeriod: string }, unknown>(
        'POST',
        `/companies/${companyId}/emp-benefit/${kind}/${v.id}/reverse`,
        { reversalPeriod: v.reversalPeriod },
      ),
    onSuccess: () => invalidateEmpBenefit(qc, companyId),
  });
}

// Propose a DRAFT monthly TAS 19 accrual (compute SC/IC, no JV). The legacy
// direct-post /monthly/run path was removed in the TAS 19 rework — posting now
// goes through propose → submit → approve (approval UI lands in a later phase),
// so this only materialises the draft and refreshes the monthly table.
export function useProposeEmpBenefitMonthly(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RunEmpBenefitMonthly) =>
      apiJson<RunEmpBenefitMonthly, EmpBenefitMonthlyEntry>(
        'POST',
        `/companies/${companyId}/emp-benefit/monthly/propose`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: monthlyKey(companyId) });
    },
  });
}
